import { useRef, useState } from 'react'
import {
  useFloating,
  useHover,
  useFocus,
  useClick,
  useInteractions,
  useDismiss,
  useRole,
  FloatingPortal,
  FloatingArrow,
  arrow,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/react'
import { Z_INDEX } from 'styles/z-index'

type TooltipTheme = 'light' | 'dark'
type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right'
type TooltipTrigger = 'hover' | 'click'

interface FloatingTooltipProps {
  children: React.ReactElement
  /** Tooltip content. Accepts plain strings or JSX — pass JSX (e.g. a list
   *  of block-level rows) when you need predictable height for scrolling. */
  text: React.ReactNode
  theme?: TooltipTheme
  placement?: TooltipPlacement
  /** Open delay in ms (close is instant). 200 by default — prevents flicker
   *  when the cursor sweeps across dense interactive clusters. Ignored when
   *  `trigger` is "click". */
  delay?: number
  /** How the tooltip opens. "hover" (default) is best for short labels;
   *  "click" is required when the tooltip content might scroll, so users can
   *  move their cursor into the tooltip and scroll without it dismissing. */
  trigger?: TooltipTrigger
  /** Classes for the trigger wrapper span. Defaults to `inline-flex`; override
   *  (e.g. `flex w-full min-w-0`) when the trigger must fill its container, such
   *  as a table cell that relies on full width for truncation. */
  triggerClassName?: string
}

export function FloatingTooltip({
  children,
  text,
  theme = 'light',
  placement = 'top',
  delay = 200,
  trigger = 'hover',
  triggerClassName = 'inline-flex',
}: FloatingTooltipProps) {
  const [open, setOpen] = useState(false)
  const arrowRef = useRef(null)
  const isDark = theme === 'dark'
  const bg = isDark ? '#1f2937' : '#ffffff'

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      flip(),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
  })

  const hover = useHover(context, {
    enabled: trigger === 'hover',
    move: false,
    delay: { open: delay, close: 0 },
  })
  const click = useClick(context, { enabled: trigger === 'click' })
  const focus = useFocus(context, { enabled: trigger === 'hover' })
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    focus,
    dismiss,
    role,
  ])

  return (
    <>
      <span
        ref={refs.setReference}
        {...getReferenceProps()}
        className={triggerClassName}
      >
        {children}
      </span>
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: Z_INDEX.TOOLTIP,
              // Radix Dialog (modal: true) uses react-remove-scroll which
              // disables pointer-events on body siblings outside the dialog.
              // FloatingPortal renders here, so without this the tooltip is
              // visible but unscrollable / unclickable / uninspectable.
              pointerEvents: 'auto',
            }}
            {...getFloatingProps()}
          >
            {/* Scroll container — its bounding box defines the visual
                tooltip. The arrow below is a sibling so it doesn't scroll
                with the content. */}
            <div
              className={[
                'rounded-lg max-w-[260px] max-h-60 overflow-y-auto',
                isDark
                  ? 'bg-neutral-800 text-white'
                  : 'bg-white text-neutral-700',
                !isDark && 'shadow-[0_1px_2px_rgba(16,24,40,0.05)]',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                // max-h-60 + overflow-y-auto on the className above; this
                // block just adds the bits Tailwind can't express inline.
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch',
              }}
              onWheel={(e) => e.stopPropagation()}
            >
              <div className="px-3 py-2 text-sm font-medium tracking-[-0.2px]">
                {text}
              </div>
            </div>
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill={bg}
              width={12}
              height={6}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
